<script setup lang="ts">
import type { IDeposit, IProps } from '~/components/entity/deposit'
import type { IResponseData } from '~/types'
import { FeaturePagination } from '~/components/feature/pagination'
import { EntityDepositItem, EntityDepositSkeleton } from '~/components/entity/deposit'
import { LucideNewspaper } from 'lucide-vue-next'
import { useFetchLocale } from '~/composables/useFetchLocale'

const emit = defineEmits<{
  loaded: [hasData: boolean]
}>()

const props = withDefaults(defineProps<IProps>(), {
  hideOrganizationDetails: false,
  hidePagination: false,
  showTypes: false,
  type: 'bank'
})

const config = useRuntimeConfig()
const baseApiUrl = config.public.baseApiUrl
const route = useRoute()

const page = ref(Number(route.query.page) || 1)

const query = computed(() =>
  new URLSearchParams({
    ...Object.fromEntries(Object.entries(props.queryParams || {}).map(([k, v]) => [k, String(v)])),
    page: String(page.value)
  }).toString()
)

const url = computed(() => `${baseApiUrl}/deposits?${query.value}`)

const {
  data: deposits,
  status
} = useFetchLocale<IResponseData<IDeposit[]>>(url, {
  key: computed(() => `deposits-${page.value}-${query.value}`)
})

watchEffect(() => {
  if (status.value === 'success') {
    emit('loaded', !!deposits.value?.data?.length)
  }
})

const mappedDeposits = computed(() =>
  deposits.value?.data?.map((item) => ({
    ...item,
    ratings: item.ratings?.average_rate ?? null
  })) ?? []
)

const fetchDeposits = (pageNumber: number) => {
  page.value = pageNumber
}
</script>

<template>
  <div>
    <section v-if="status === 'success'" aria-labelledby="deposits">
      <div v-if="mappedDeposits.length" class="space-y-3">
        <Heading id="deposits" tag="h2" class="sr-only">{{ $t('common.deposits') }}</Heading>
        <template v-for="item in mappedDeposits" :key="item.id">
          <EntityDepositItem
            :item="item"
            :hide-organization-details="props.hideOrganizationDetails"
            :show-types="props.showTypes"
            :type="props.type"
            :position="props.position"
          />
        </template>
        <div
          v-if="!props.hidePagination && deposits.meta.last_page !== 1"
          class="mt-7 flex justify-end"
        >
          <FeaturePagination
            :meta="deposits.meta"
            :links="deposits.links"
            @change="fetchDeposits"
          />
        </div>
      </div>
      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('widget.deposits.list.no_deposits') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else>
      <EntityDepositSkeleton
        :count="Number(props.queryParams?.per_page || 3)"
        :hide-organization-details="props.hideOrganizationDetails"
      />
    </section>
  </div>
</template>
